import { isCurrentQliveOrigin } from './resolveQliveOrigin';

const MESSAGE_TYPE = 'qlive-device-online-notification';

let bridgeIframe = null;
let bridgeReady = false;
let bridgeOrigin = '';
let pendingPayloads = [];

function postToBridge(payload) {
    if (!bridgeIframe?.contentWindow || !bridgeOrigin) {
        return false;
    }

    try {
        bridgeIframe.contentWindow.postMessage({
            type: MESSAGE_TYPE,
            payload,
        }, bridgeOrigin);

        return true;
    } catch (error) {
        return false;
    }
}

function flushPendingPayloads() {
    if (!bridgeReady) {
        return;
    }

    pendingPayloads.forEach((payload) => {
        postToBridge(payload);
    });
    pendingPayloads = [];
}

function showDirectNotification(payload) {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
        return false;
    }

    try {
        const notification = new Notification(payload.title || 'Device online', {
            body: payload.body || '',
            tag: payload.tag,
            renotify: payload.renotify !== false,
            icon: payload.icon || undefined,
        });

        notification.onclick = () => {
            window.focus();
            notification.close();
        };

        return true;
    } catch (error) {
        return false;
    }
}

export function ensureQliveNotificationBridge(qliveOrigin) {
    const normalizedOrigin = (qliveOrigin || '').replace(/\/+$/, '');

    if (!normalizedOrigin || isCurrentQliveOrigin(normalizedOrigin)) {
        return Promise.resolve(true);
    }

    if (typeof document === 'undefined') {
        return Promise.resolve(false);
    }

    bridgeOrigin = normalizedOrigin;

    if (bridgeIframe && bridgeReady) {
        return Promise.resolve(true);
    }

    return new Promise((resolve) => {
        let settled = false;

        const markReady = () => {
            if (settled) {
                return;
            }

            settled = true;
            bridgeReady = true;
            flushPendingPayloads();
            resolve(true);
        };

        const onReadyMessage = (event) => {
            if (event.origin !== bridgeOrigin) {
                return;
            }

            if (event.data?.type === 'qlive-notification-bridge-ready') {
                window.removeEventListener('message', onReadyMessage);
                markReady();
            }
        };

        window.addEventListener('message', onReadyMessage);

        if (!bridgeIframe) {
            bridgeIframe = document.createElement('iframe');
            bridgeIframe.src = `${bridgeOrigin}/notificationBridge.html`;
            bridgeIframe.title = 'Qlive notification bridge';
            bridgeIframe.setAttribute('aria-hidden', 'true');
            bridgeIframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none';
            document.body.appendChild(bridgeIframe);
        }

        window.setTimeout(() => {
            window.removeEventListener('message', onReadyMessage);
            if (!settled) {
                settled = true;
                resolve(bridgeReady);
            }
        }, 8000);
    });
}

export async function requestQliveNotificationPermission(qliveOrigin) {
    if (!('Notification' in window)) {
        return 'unsupported';
    }

    let localResult = Notification.permission;
    if (Notification.permission === 'default') {
        try {
            localResult = await Notification.requestPermission();
        } catch (error) {
            localResult = Notification.permission;
        }
    }

    if (!isCurrentQliveOrigin(qliveOrigin)) {
        await ensureQliveNotificationBridge(qliveOrigin);
    }

    return localResult;
}

export function showDeviceOnlineNotificationViaQlive(payload, qliveOrigin) {
    const normalizedOrigin = (qliveOrigin || payload?.qliveOrigin || '').replace(/\/+$/, '');
    const messagePayload = {
        ...payload,
        qliveOrigin: normalizedOrigin,
        renotify: payload?.renotify !== false,
    };

    if (isCurrentQliveOrigin(normalizedOrigin)) {
        return showDirectNotification(messagePayload);
    }

    if (showDirectNotification(messagePayload)) {
        return true;
    }

    if (bridgeReady && postToBridge(messagePayload)) {
        return true;
    }

    // Bridge non pronto: restituisci false così releaseNotificationSlot libera il
    // dedup e la prossima tab (o il prossimo poll) potrà riprovare.
    return false;
}
